import {Inject, Injectable, OnDestroy} from '@angular/core';
import { merge, Observable, Subject, Subscription} from "rxjs";
import {WINDOW_LISTENER_TOKEN, WindowEvent, WindowHostListenerService} from "./window.host.listener.service";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {debounceTime, filter, first, tap} from "rxjs/operators";
import {LogCat, LogManager} from "../log";

const observablesToCheck =[
	WindowEvent.mousemove,
	WindowEvent.mousedown,
	WindowEvent.touchstart,
	WindowEvent.click,
	WindowEvent.scroll,
	WindowEvent.keypress];

@UntilDestroy()
@Injectable()
export class WorkstationLockDetectionService implements OnDestroy {
	readonly MANUAL_LOCK="MANUAL_LOCK";
	readonly MANUAL_LOCK_WITH_UNLOCK="MANUAL_LOCK_WITH_UNLOCK";
	readonly MANUAL_UNLOCK="MANUAL_UNLOCK";

	private inactiveDetected:Subject<Date|String>;

	// not needed because user can lock workstation even with sem connected
	// checks are done in sem communication service
	// private userLockEnabled: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

	private t:number;
	private lastDetection:Date;
	private inactiveIntervalAllowedMillis;

	private subscription:Subscription;
	private manualLockEnable:boolean;

	private disableDebounceForActivityCheck=false;

	private isDisabled;
	private _windowActivityObservable : Observable<any>;

	constructor(@Inject(WINDOW_LISTENER_TOKEN) private windowListener: WindowHostListenerService) {
		this.inactiveDetected = new Subject<Date>();
		//this.isDisabled=false;
		this.manualLockEnable=false;
	}

	// public disableActivityChecks(){
	// 	this.isDisabled=true;
	// }

	// for iFrame mode the debounce doesnt work
	public disableDebounceForActivity(){
		this.disableDebounceForActivityCheck=true;
	}

	public get WindowActivityObservable(): Observable<any>{
		if (!this._windowActivityObservable) {
			this._windowActivityObservable = this.getWindowActivitiesObservable();
			if (!this.disableDebounceForActivityCheck){
				this._windowActivityObservable=this._windowActivityObservable.pipe(debounceTime(2000));
			}
		}
		return this._windowActivityObservable;
	}


	// if secs===0 then we only respond to manual mode locks
	public activate(secs:number){
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
		if (secs&&!this.manualLockEnable) {
			this.inactiveIntervalAllowedMillis = secs * 1000;
			this.resetTimer(this.inactiveIntervalAllowedMillis);
			this.lastDetection = new Date();

			this.subscription = this.getWindowActivitiesObservable()
				.pipe(tap(x => this.lastDetection = new Date()), untilDestroyed(this)) // not really required...
				.subscribe();
		}
		return this.observe();
	}

	public ngOnDestroy(): void {
		this.deactivate(false);
	}

	public manualLock(isUnlockDriver) {
		if (!this.manualLockEnable){
			LogManager.error(LogCat.AppShell.Sessions,"Locking is not in manual mode. Cannot proceed with lock");
		}else {
			this.inactiveDetected.next(isUnlockDriver?this.MANUAL_LOCK_WITH_UNLOCK: this.MANUAL_LOCK);
		}

	}

	public manualUnLock() {
		if (!this.manualLockEnable) {
			LogManager.error(LogCat.AppShell.Sessions,"Locking is not in manual mode. Cannot proceed with lock");
		} else {
			this.inactiveDetected.next(this.MANUAL_UNLOCK);
		}
	}

	public deactivate(enableManualLock:boolean){
		this.manualLockEnable=enableManualLock;
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
		if (!enableManualLock && this.inactiveDetected){
			this.inactiveDetected.complete();
			this.inactiveDetected = null;
		}
		this.isDisabled=true;
		this.clearTimer();

		// not needed because user can lock workstation even with sem connected
		// checks are done in sem communication service
		// this.userLockEnabled.next(false);
	}

	public userLock() {
		this.inactiveDetected.next(new Date());
	}

	// not needed because user can lock workstation even with sem connected
	// checks are done in sem communication service
	// get $userLockEnabled(): Observable<boolean> {
	// 	return this.userLockEnabled;
	// }

	private getWindowActivitiesObservable(): Observable<any> {
		return  merge(...observablesToCheck.map(x=>this.windowListener.getObservable(x)));
	}

	private observe(){
		return merge(this.inactiveDetected.asObservable().pipe(filter(x=>x instanceof Date),first()),
			this.inactiveDetected.asObservable().pipe(filter(x=>!(x instanceof Date))));
	}

	private clearTimer(){
		if (this.t){
			clearTimeout(this.t);
			this.t=0;
		}
	}


	private resetTimer(milis) {
		this.clearTimer();
		this.t = <any> setTimeout(this.checkActivity, milis);
	}

	private checkActivity = ()=>{
		this.t = null;
		if (!this.isDisabled) {
			if (Math.abs(<any>new Date() - <any>this.lastDetection) > this.inactiveIntervalAllowedMillis) {
				this.inactiveDetected.next(this.lastDetection);
			} else {
				this.resetTimer(Math.max(this.inactiveIntervalAllowedMillis - Math.abs(<any>new Date() - <any>this.lastDetection), 1000));
			}
		}
		// milisWaited is the sleep time. we want to get the actual last user detection
		//let timeIdle = Math.abs( new Date().getMilliseconds() - this.lastDetection.getMilliseconds());

	}
}
